/**
 * KnowledgeGraph — `react-force-graph-2d` 기반 canvas 그래프 렌더러.
 *
 * 도입 배경:
 * Cosmograph 2.x (2.1 ~ 2.3-beta) 가 이 프로젝트의 React 19 + Vite 6 환경에서 `_rebuildGraph`
 * 진입 시점에 `Object.keys(null)` 로 crash 하는 회귀가 재현됐다 (로컬 + Lambda 배포 양쪽 확인).
 * data 포맷·config 조합을 여러 번 바꿔도 동일하게 실패해 라이브러리를 교체한다.
 *
 * react-force-graph-2d 는 d3-force + canvas 기반으로 경량이고 React 19 와도 호환된다.
 * - 외부 호출 shape(`nodes`, `edges`, `onNodeSelect`, `onNodeDblClick`, `search`, `selectedKind`,
 *   `enabledLayers`) 는 기존과 동일하게 유지 — GraphTab / BlocksPage 는 재작업 없이 그대로 사용.
 * - `KnowledgeGraphHandle`(`fitView`, `zoomToNode`) 도 유지.
 *
 * 원본: `src/dashboard/src/components/graph/KnowledgeGraph.tsx` 에서 shared 로 이전.
 * 대시보드와 docs-site playground 가 공용한다.
 *
 * SSR/CSR 경계: `window`/`document` 직접 참조는 없음. `react-force-graph-2d` 가 브라우저
 * 전용이므로 Docusaurus 에서는 BrowserOnly 래퍼를 통해 사용한다 (Phase 5).
 */
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react"
import ForceGraph2D, { type ForceGraphMethods } from "react-force-graph-2d"
import { getNodeColor, KIND_TO_LAYER, LAYERS, prepareGraphData } from "./graph-utils.ts"
import type { GraphEdge, GraphNode } from "./types.ts"

export interface KnowledgeGraphHandle {
	fitView: () => void
	zoomToNode: (nodeId: string) => void
}

export interface KnowledgeGraphProps {
	readonly nodes: ReadonlyArray<GraphNode>
	readonly edges: ReadonlyArray<GraphEdge>
	readonly onNodeSelect?: (node: GraphNode | null) => void
	readonly onNodeDblClick?: (node: GraphNode) => void
	/** 엣지(link) 클릭 — `{fromId}|{relation}|{toId}` edgeKey 구성에 사용. */
	readonly onLinkSelect?: (fromId: string, toId: string, relation: string) => void
	readonly search?: string
	readonly selectedKind?: string
	readonly enabledLayers?: ReadonlySet<string>
	/** 강조할 edgeKey — `{fromId}|{relation}|{toId}` 형식. 매칭 엣지는 굵은 색으로 렌더. */
	readonly highlightedEdgeKey?: string
	/**
	 * 컨테이너 fill 모드.
	 *  - "viewport" (기본, dashboard): outer div 가 `calc(100vh - 200px)` + `minHeight 520` 고정
	 *  - "container": outer div 가 부모 100% 를 따라감 — docs MDX 의 `<GraphEmbed height={420}/>` 처럼
	 *    고정 높이 컨테이너 안에 삽입되는 경우 사용
	 */
	readonly fill?: "viewport" | "container"
	/**
	 * 초기 fit 의 zoom 상한. 노드 2~3 개만 있는 미니 그래프에서 과확대를 막는다.
	 * 기본 Infinity (제한 없음) — docs embed 는 1.5 ~ 2.0 을 추천.
	 */
	readonly maxInitialZoom?: number
	/**
	 * relation name → 한국어 displayName 맵. 미제공 시 raw name 노출.
	 * 엣지 중앙 라벨 렌더에 사용됨 — `MEMBER_OF` 가 raw 로 찍히는 시인성 문제 해결.
	 */
	readonly relationDisplayNames?: ReadonlyMap<string, string>
	/**
	 * kind name → 한국어 displayName 맵. 노드 호버 툴팁에 사용됨.
	 * 미제공 시 raw kind 노출.
	 */
	readonly kindDisplayNames?: ReadonlyMap<string, string>
	/**
	 * relation name → directionality. `"undirected"` 인 엣지는 화살표 표시를 숨긴다.
	 * 미제공이거나 `"directed"` 면 화살표 렌더 (기본).
	 * 출처: 온톨로지 슬라이스의 `RelationDef.directionality` 필드.
	 */
	readonly relationDirectionality?: ReadonlyMap<string, "directed" | "undirected">
	/**
	 * 엣지 화살표 기본 길이 (강조되지 않은 directed 엣지 기준).
	 * 기본 3 — 대시보드에 적합. docs MDX `<GraphEmbed>` (zoom 1.6) 는 6 추천.
	 * 강조 엣지는 이 값의 1.65배로 렌더.
	 */
	readonly arrowLength?: number
}

// force-graph 는 link 의 source/target 을 실제 노드 객체로 상호교체하는 변성 동작이 있으므로,
// 입력을 방어적으로 복제한 뒤 `{ source: string, target: string }` 형태만 넘긴다.
interface FgNode extends GraphNode {
	readonly x?: number
	readonly y?: number
}
interface FgLink {
	readonly source: string
	readonly target: string
	readonly relation: string
	readonly _color: string
}

function toFgLinks(edges: ReadonlyArray<GraphEdge>): FgLink[] {
	return edges.map((e) => ({
		source: e.source,
		target: e.target,
		relation: e.relation,
		_color: e._color || "#4b5563"
	}))
}

/**
 * 엣지 식별 키 — `{fromId}|{relation}|{toId}` 형식. source/target 이 런타임에 노드 객체로
 * 교체될 수 있으므로 양쪽 케이스를 처리한다.
 */
function buildEdgeKey(l: FgLink): string {
	const src = typeof l.source === "string" ? l.source : ((l.source as unknown as FgNode)?.id ?? "")
	const tgt = typeof l.target === "string" ? l.target : ((l.target as unknown as FgNode)?.id ?? "")
	return `${src}|${l.relation}|${tgt}`
}

export const KnowledgeGraph = forwardRef<KnowledgeGraphHandle, KnowledgeGraphProps>(function KnowledgeGraph(
	{
		nodes,
		edges,
		onNodeSelect,
		onNodeDblClick,
		onLinkSelect,
		search = "",
		selectedKind,
		enabledLayers,
		highlightedEdgeKey,
		fill = "viewport",
		maxInitialZoom,
		relationDisplayNames,
		kindDisplayNames,
		relationDirectionality,
		arrowLength
	},
	ref
) {
	const fgRef = useRef<ForceGraphMethods<FgNode, FgLink> | undefined>(undefined)
	const containerRef = useRef<HTMLDivElement>(null)
	const [containerSize, setContainerSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 })
	const lastClickRef = useRef<{ id: string; time: number }>({ id: "", time: 0 })
	// Timeline Playback 시 asOf 변경마다 노드/엣지 조합이 달라져 dataSignature 가 튐 → force reheat.
	// 작은 diff (asOf tick 수준) 에선 기존 좌표 유지, 초기 mount 나 대규모 churn (>30%) 에서만 전체 reheat.
	// (레이아웃 계산 오버레이는 제거 — overview limit 100 기준 settle 이 짧아 live 수렴을 그대로 보여주는 편이 낫다.)
	const prevNodeIdsRef = useRef<Set<string>>(new Set())
	const firstMountRef = useRef(true)
	// 초기 수렴 시 1 회만 zoomToFit. 이후 reheat(asOf/데이터 변경)에서는 사용자 viewport 를 보존한다.
	// 수동으로 재정렬이 필요하면 KnowledgeGraphHandle.fitView() 호출 (툴바 Fit 버튼).
	const hasAutoFitRef = useRef(false)

	// 컨테이너 크기 — ResizeObserver 로 반응형
	useEffect(() => {
		if (!containerRef.current) return
		const el = containerRef.current
		const ro = new ResizeObserver(() => {
			const rect = el.getBoundingClientRect()
			setContainerSize({ w: rect.width, h: rect.height })
		})
		ro.observe(el)
		const rect = el.getBoundingClientRect()
		setContainerSize({ w: rect.width, h: rect.height })
		return () => ro.disconnect()
	}, [])

	const dataSignature = useMemo(
		() =>
			`${nodes.length}:${nodes[0]?.id ?? ""}:${nodes[nodes.length - 1]?.id ?? ""}:${edges.length}:${edges[0]?.id ?? ""}:${edges[edges.length - 1]?.id ?? ""}`,
		[nodes, edges]
	)

	// biome-ignore lint/correctness/useExhaustiveDependencies: dataSignature 로 구조적 동등성 판정
	const prepared = useMemo(() => prepareGraphData(nodes, edges), [dataSignature])

	const filteredData = useMemo(() => {
		let ns = prepared.nodes
		let ls = prepared.links

		if (enabledLayers && enabledLayers.size > 0) {
			ns = ns.filter((n) => {
				const layer = KIND_TO_LAYER[n.kind]
				return layer ? enabledLayers.has(layer) : true
			})
		}

		if (search.trim()) {
			const q = search.toLowerCase()
			ns = ns.filter(
				(n) =>
					n.name.toLowerCase().includes(q) ||
					n.kind.toLowerCase().includes(q) ||
					n.domain.toLowerCase().includes(q)
			)
		}

		if (selectedKind) {
			ns = ns.filter((n) => n.kind === selectedKind)
		}

		if (search.trim() || selectedKind || (enabledLayers && enabledLayers.size > 0)) {
			const nodeIds = new Set(ns.map((n) => n.id))
			ls = ls.filter((l) => nodeIds.has(l.source) && nodeIds.has(l.target))
		}

		return { nodes: ns as FgNode[], links: toFgLinks(ls) }
	}, [prepared, search, selectedKind, enabledLayers])

	const filteredRef = useRef(filteredData)
	filteredRef.current = filteredData

	useImperativeHandle(
		ref,
		() => ({
			fitView: () => {
				// 짧은 duration (느리게 보이던 400 → 150ms) + 넉넉한 padding (40 → 120) 으로
				// "Fit" 버튼 반응을 즉각적으로 + 노드가 캔버스 가장자리에 붙지 않도록.
				fgRef.current?.zoomToFit(150, 120)
			},
			zoomToNode: (nodeId: string) => {
				const node = filteredRef.current.nodes.find((n) => n.id === nodeId)
				if (!node) return
				// force-graph 는 시뮬레이션 도중 x/y 좌표를 각 노드 객체에 주입한다.
				const n = node as FgNode & { x?: number; y?: number }
				if (typeof n.x === "number" && typeof n.y === "number") {
					fgRef.current?.centerAt(n.x, n.y, 400)
					fgRef.current?.zoom(3, 400)
				}
			}
		}),
		[]
	)

	// data 변경 시 force 파라미터 조정(라벨 겹침 방지) + 초기 zoom.
	//  - link distance / charge / collide 값을 **강하게** 잡아 노드 간 물리적 거리 확보.
	//  - 시뮬레이션 안정 후 zoomToFit 으로 전체 화면에 맞춰 스크린 활용 극대화.
	//    (이전에는 zoom 1.2 고정이었는데 노드가 100+ 개 규모에서 중앙 뭉침 심각해서
	//    강한 force + fit-to-screen 조합으로 전환.)
	// biome-ignore lint/correctness/useExhaustiveDependencies: dataSignature 로 구조적 동등성 판정
	useEffect(() => {
		const fg = fgRef.current
		// containerSize 가 0 이면 <ForceGraph2D> 가 아직 mount 전이라 fg === undefined.
		// 이 경우 force 설정이 유실되므로 effect 가 다시 실행되도록 dependency 에
		// containerSize.w/h 를 포함한다 (ResizeObserver 가 값을 채우면 재시도).
		if (!fg || containerSize.w === 0 || containerSize.h === 0) return
		// 이전 노드 id 집합 대비 diff 비율 계산 — 소폭 변경 (≤30% 교체) 에선 reheat 생략.
		const currentIds = new Set(filteredRef.current.nodes.map((n) => n.id))
		const prevIds = prevNodeIdsRef.current
		const union = new Set([...prevIds, ...currentIds])
		const intersection = [...prevIds].filter((id) => currentIds.has(id))
		const churnRatio =
			union.size === 0 ? 1 : 1 - intersection.length / Math.max(prevIds.size, currentIds.size, 1)
		const isInitialMount = firstMountRef.current
		const isLargeChurn = churnRatio > 0.3
		prevNodeIdsRef.current = currentIds
		firstMountRef.current = false
		// 서비스 전환/대규모 churn 시 사용자 viewport 는 더 이상 유효하지 않으므로 auto-fit 을 다시 허용.
		// 소폭 churn(asOf 변경 등) 은 뷰포트 유지 — 사용자가 줌/팬 한 상태를 보존.
		if (isLargeChurn) hasAutoFitRef.current = false
		if (fg) {
			// 반발력은 과격하지 않게, 대신 link 거리를 충분히 확보 + link strength 를
			// 적당히(0.5) 두어 구조가 튀지 않고 안정적으로 퍼진다.
			// Hub 노드(여러 링크를 가진 Service/Deployment 등)의 중앙 수렴을 상쇄하기 위해
			// link strength 를 node degree 역수로 스케일 — hub 일수록 각 링크가 약하게 당긴다.
			const degree = new Map<string, number>()
			for (const l of filteredRef.current.links) {
				degree.set(l.source, (degree.get(l.source) ?? 0) + 1)
				degree.set(l.target, (degree.get(l.target) ?? 0) + 1)
			}
			// d3-force 표준 권장값: 100+ 노드에선 charge -80 + distanceMin 25. 과한 값(-2400+)
			// 은 simulation 이 튕기며 수렴 실패한다. link distance 는 기본 30 의 몇 배 수준.
			fg.d3Force("link")
				?.distance(120)
				.strength((l: FgLink) => {
					const ds = typeof l.source === "string" ? l.source : (l.source as FgNode).id
					const dt = typeof l.target === "string" ? l.target : (l.target as FgNode).id
					const maxDeg = Math.max(degree.get(ds) ?? 1, degree.get(dt) ?? 1)
					return 0.6 / Math.max(maxDeg, 1)
				})
			fg.d3Force("charge")?.strength(-1600).distanceMin(35).distanceMax(1200).theta(0.9)
			fg.d3Force("center")?.strength(0)
			import("d3-force").then(({ forceCollide, forceX, forceY }) => {
				const R = 900
				const anchor = new Map<string, [number, number]>()
				LAYERS.forEach((l, i) => {
					const a = (i / LAYERS.length) * Math.PI * 2 - Math.PI / 2
					anchor.set(l, [Math.cos(a) * R, Math.sin(a) * R])
				})
				const getAnchor = (n: FgNode) => anchor.get(KIND_TO_LAYER[n.kind] ?? "detail") ?? [0, 0]
				fg.d3Force("x", forceX((n: FgNode) => getAnchor(n)[0]).strength(0.25))
				fg.d3Force("y", forceY((n: FgNode) => getAnchor(n)[1]).strength(0.25))
				fg.d3Force("collide", forceCollide(52).strength(1).iterations(2))
				// 대규모 변경이나 초기 mount 에서만 전체 reheat — 소폭 변경은 library 가 내부적으로 처리.
				if (isInitialMount || isLargeChurn) fg.d3ReheatSimulation()
			})
		}
		// zoomToFit 는 `cooldownTime` 도달 또는 `d3AlphaMin` 수렴 시 `onEngineStop` 에서 처리.
	}, [dataSignature, containerSize.w, containerSize.h])

	const hasData = filteredData.nodes.length > 0 && filteredData.links.length > 0
	const nodesOnly = filteredData.nodes.length > 0 && filteredData.links.length === 0

	// "container" 모드: 부모가 고정 높이를 부여 — docs MDX GraphEmbed 처럼 inline 삽입.
	// "viewport" 모드: 대시보드 standalone 캔버스 — 화면 크기 기준 자동 확장.
	const outerStyle =
		fill === "container"
			? ({
					position: "relative",
					width: "100%",
					height: "100%",
					overflow: "hidden"
				} as const)
			: ({
					position: "relative",
					width: "100%",
					height: "calc(100vh - 200px)",
					maxHeight: "calc(100vh - 200px)",
					minHeight: 520,
					overflow: "hidden"
				} as const)

	return (
		<div ref={containerRef} style={outerStyle}>
			{hasData && containerSize.w > 0 && containerSize.h > 0 && (
				<div>
					<ForceGraph2D
						ref={fgRef}
						width={containerSize.w}
						height={containerSize.h}
						graphData={filteredData}
						backgroundColor="#ffffff"
						nodeId="id"
						nodeLabel={(n) => {
							const node = n as FgNode
							const kindLabel = kindDisplayNames?.get(node.kind) ?? node.kind
							return `${node.name} — ${kindLabel}`
						}}
						nodeColor={(n) => getNodeColor(n as FgNode)}
						nodeRelSize={4}
						nodeVal={(n) => Math.max(1, (n as FgNode)._size ?? 1)}
						linkColor={(l) => {
							const fgl = l as FgLink
							const key = buildEdgeKey(fgl)
							if (highlightedEdgeKey && key === highlightedEdgeKey) {
								// 강조된 엣지 — 원색 그대로 (불투명).
								return fgl._color || "#4b5563"
							}
							// 반투명 처리 — hex 를 rgba 로 변환해 visual noise 감소 (391 edges 기준).
							const c = fgl._color || "#4b5563"
							return c.length === 7 ? `${c}73` : c // 8-digit hex (~45% alpha)
						}}
						linkWidth={(l) => (buildEdgeKey(l as FgLink) === highlightedEdgeKey ? 2.4 : 0.8)}
						linkDirectionalArrowLength={(l) => {
							const fgl = l as FgLink
							// undirected relation 은 화살표를 그리지 않아 양방향 의미를 시각적으로 구분.
							if (relationDirectionality?.get(fgl.relation) === "undirected") return 0
							const base = arrowLength ?? 3
							return buildEdgeKey(fgl) === highlightedEdgeKey ? base * 1.65 : base
						}}
						linkDirectionalArrowRelPos={1}
						// 엣지 relation 라벨을 엣지 중앙에 캔버스로 렌더. 줌이 많이 빠지면 시각적 노이즈라 스킵.
						linkCanvasObjectMode={() => "after"}
						linkCanvasObject={(link, ctx, globalScale) => {
							// force-graph 는 런타임에 source/target 을 노드 객체로 치환한다. 선언 타입은 string.
							const l = link as unknown as {
								source: FgNode & { x?: number; y?: number }
								target: FgNode & { x?: number; y?: number }
								relation: string
							}
							// raw relation name → 한국어 displayName 으로 치환 (없으면 raw 폴백).
							const rawRelation = l.relation
							const label = relationDisplayNames?.get(rawRelation) ?? rawRelation
							if (!label) return
							if (globalScale < 0.7) return
							const src = l.source
							const tgt = l.target
							if (typeof src?.x !== "number" || typeof src?.y !== "number") return
							if (typeof tgt?.x !== "number" || typeof tgt?.y !== "number") return
							const mx = (src.x + tgt.x) / 2
							const my = (src.y + tgt.y) / 2
							const fontSize = Math.max(8, Math.min(11, 10 / globalScale))
							ctx.font = `${fontSize}px -apple-system, system-ui, sans-serif`
							ctx.textAlign = "center"
							ctx.textBaseline = "middle"
							const textW = ctx.measureText(label).width
							ctx.fillStyle = "rgba(255, 255, 255, 0.9)"
							ctx.fillRect(mx - textW / 2 - 3, my - fontSize / 2 - 1, textW + 6, fontSize + 2)
							ctx.fillStyle = "#6b7684"
							ctx.fillText(label, mx, my)
						}}
						warmupTicks={60}
						cooldownTicks={Number.POSITIVE_INFINITY}
						// 이전 cooldownTime=6000 + d3AlphaMin=0.002 조합은 작은 그래프(10~20 노드)
						// 에서도 fitView 까지 5~6 초가 걸려 체감이 느렸다. α 감쇠율을 높이고 종료
						// 임계값을 완화해 ~1.5 초 내 수렴. 대규모(수백 노드) 그래프는 여전히
						// cooldownTime 상한 2500ms 이내에 1차 배치가 나오며, 이후 사용자 조작.
						cooldownTime={2500}
						d3AlphaDecay={0.035}
						d3AlphaMin={0.01}
						d3VelocityDecay={0.3}
						autoPauseRedraw
						onEngineStop={() => {
							// 최초 수렴 1 회만 전체 화면에 맞춤. 이후 asOf/데이터 변경으로 engine 이
							// 다시 멈출 때는 사용자가 직접 확대/이동한 viewport 를 유지한다.
							if (!hasAutoFitRef.current) {
								const fg = fgRef.current
								if (fg) {
									// 작은 캔버스 (docs MDX 240~420px) 에서는 padding 60px 이 과하고 노드가 잘린다.
									// 컨테이너 짧은 쪽의 ~12% 를 padding 상한으로 재계산.
									const minSide = Math.min(containerSize.w || 0, containerSize.h || 0)
									const pad = minSide > 0 ? Math.max(16, Math.min(60, Math.round(minSide * 0.12))) : 60
									fg.zoomToFit(300, pad)
									// 노드 2~3 개만 있는 미니 그래프는 fit 결과 zoom 이 8+ 까지 튀어 노드가 canvas
									// 를 넘어 보인다. 상한을 두면 작은 그래프도 "적당한 크기" 로 정돈.
									if (maxInitialZoom !== undefined) {
										queueMicrotask(() => {
											const current = fg.zoom()
											if (current > maxInitialZoom) fg.zoom(maxInitialZoom, 200)
										})
									}
								}
								hasAutoFitRef.current = true
							}
						}}
						// 노드 라벨을 캔버스에 항상 렌더 — 색상 원 + 아래쪽 텍스트.
						// 줌 레벨이 낮아질수록 폰트 크기를 역으로 키워 가독성 유지.
						nodeCanvasObjectMode={() => "after"}
						nodeCanvasObject={(node, ctx, globalScale) => {
							const n = node as FgNode & { x?: number; y?: number }
							if (typeof n.x !== "number" || typeof n.y !== "number") return
							const label = n.name || n.id
							const fontSize = Math.max(11, Math.min(16, 13 / globalScale))
							ctx.font = `${fontSize}px -apple-system, system-ui, sans-serif`
							ctx.textAlign = "center"
							ctx.textBaseline = "top"
							const size = Math.max(1, n._size ?? 1)
							const radius = Math.sqrt(size) * 4
							const y = n.y + radius + 2
							// 가독성용 반투명 배경 (라이트 테마 — 흰 배경 위 다크 텍스트)
							const textW = ctx.measureText(label).width
							ctx.fillStyle = "rgba(255, 255, 255, 0.88)"
							ctx.fillRect(n.x - textW / 2 - 3, y - 1, textW + 6, fontSize + 3)
							ctx.fillStyle = "#191f28"
							ctx.fillText(label, n.x, y)
						}}
						onNodeClick={(node) => {
							const n = node as FgNode
							const now = performance.now()
							const isDbl = n.id === lastClickRef.current.id && now - lastClickRef.current.time < 300
							lastClickRef.current = { id: n.id, time: now }
							if (isDbl) {
								onNodeDblClick?.(n)
								return
							}
							onNodeSelect?.(n)
						}}
						onLinkClick={(link) => {
							const l = link as unknown as FgLink
							const src =
								typeof l.source === "string" ? l.source : ((l.source as unknown as FgNode)?.id ?? "")
							const tgt =
								typeof l.target === "string" ? l.target : ((l.target as unknown as FgNode)?.id ?? "")
							if (!src || !tgt) return
							onLinkSelect?.(src, tgt, l.relation)
						}}
					/>
				</div>
			)}
			{filteredData.nodes.length === 0 && (
				<div
					style={{
						position: "absolute",
						inset: 0,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						color: "var(--muted, #6b7684)",
						fontSize: 13
					}}
				>
					표시할 노드가 없습니다.
				</div>
			)}
			{nodesOnly && (
				<div
					style={{
						position: "absolute",
						inset: 0,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						flexDirection: "column",
						gap: 6,
						color: "var(--muted, #6b7684)",
						fontSize: 13,
						padding: 24,
						textAlign: "center"
					}}
				>
					<strong style={{ fontSize: 14, color: "var(--text, #191f28)" }}>연결(edge) 이 아직 없습니다</strong>
					<span>
						노드 {filteredData.nodes.length}개 중 서로 연결된 shape(relation) 이 저장되지 않아 그래프를 그리지
						못합니다. knowledge_upsert 로 edge 를 추가하거나 filter 를 풀어보세요.
					</span>
				</div>
			)}
		</div>
	)
})
